import React, { useEffect, useState } from 'react';
import { push } from 'connected-react-router';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from 'store';
import Home from './home';
import useAuth from 'hooks/useAuth';
import AnimatedLogo from 'components/animatedLogo';
import DustBackground from 'components/shell/DustBackground';
import { ui, button } from 'util/ui';

/**
 * Sign-in page, UI refresh (5 Nov 2026): the light Archy page with the archive dust, and a white card
 * with the animated CoBox logo on a pink band. The customer's own Settings can't be read before
 * sign-in (the database rules), so the page stays generic apart from the logo. Behaviour unchanged.
 */

const field: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 600, color: ui.text2 };
const input: React.CSSProperties = {
  height: 44,
  boxSizing: 'border-box',
  border: `1px solid ${ui.control}`,
  borderRadius: 10,
  padding: '0 12px',
  fontFamily: 'inherit',
  fontSize: 15,
  fontWeight: 400,
  color: ui.ink,
  background: ui.surface,
  width: '100%',
};

const Login = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: AppState) => state.auth);
  const { signIn } = useAuth();

  const [wrongEorP, setWrongEorP] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);
    setWrongEorP(false);
    const signedIn = await signIn(email, password);
    if (!signedIn) {
      setWrongEorP(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      dispatch(push('/'));
    }
  }, [user]);

  if (user) return <Home />;

  return (
    <div style={{ minHeight: '100vh', background: ui.page, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, boxSizing: 'border-box', fontFamily: ui.font }}>
      <DustBackground />
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          width: 'min(400px, 100%)',
          background: ui.surface,
          border: `1px solid ${ui.line}`,
          borderRadius: 18,
          boxShadow: '0 24px 60px rgba(22,24,29,0.12)',
          overflow: 'hidden',
        }}
      >
        <div style={{ background: `linear-gradient(135deg, ${ui.accent}, #9E1269)`, padding: '26px 24px 18px', display: 'flex', justifyContent: 'center' }}>
          <div style={{ width: 180 }}>
            <AnimatedLogo pulseDelay={3000} />
          </div>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: '22px 24px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: ui.ink, letterSpacing: '-0.01em' }}>Sign in</h1>
            <p style={{ margin: '4px 0 0', fontSize: 14, color: ui.muted }}>Use the email and password from your invitation.</p>
          </div>
          <label style={field}>
            Email
            <input
              name="email"
              type="email"
              autoComplete="username"
              required
              placeholder="name@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={input}
            />
          </label>
          <label style={field}>
            Password
            <input
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={input}
            />
          </label>
          {wrongEorP && (
            <div role="alert" style={{ fontSize: 14, color: '#8C1D18', background: '#FDECEA', border: '1px solid #F3C7C3', borderRadius: 10, padding: '10px 12px' }}>
              That email and password don't match. Check them and try again.
            </div>
          )}
          <button
            type="submit"
            className="archy-btn"
            disabled={loading}
            style={{ ...button('primary'), height: 46, justifyContent: 'center', width: '100%', fontSize: 15, opacity: loading ? 0.7 : 1, cursor: loading ? 'wait' : 'pointer' }}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
          <p style={{ margin: 0, fontSize: 13, color: ui.muted }}>
            Forgotten your password? Ask your administrator for a reset link.
          </p>
        </form>
        <div style={{ padding: '10px 24px', borderTop: `1px solid ${ui.lineSoft}`, background: ui.field, fontSize: 12, color: ui.muted, textAlign: 'center' }}>
          Powered by <strong style={{ color: ui.ink }}>Archy</strong>
        </div>
      </div>
    </div>
  );
};

export default Login;
